import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

/**
 * Global toast notifications — a web port of the native iOS Toast
 * utility. A single <ToastHost/> mounts once at the app root; anywhere
 * else you call the imperative API:
 *
 *   import { toast } from '@design-system/components';
 *   toast.success('Saved');
 *   toast.error('That didn’t work');
 *
 * Like the native version, only one toast is on screen at a time —
 * extra calls queue and play back in order. Calls made before the host
 * mounts are buffered, so it is always safe to call.
 *
 * These pages live on a cream background, so the toast itself is the
 * dark, blurred "system material" pill from the native design; type
 * only drives the icon + its accent colour (pulled from brand tokens),
 * not the surface.
 *
 * It arrives from the top for every caller. The bottom edge is where the
 * composer, the finish row and the phone's own home indicator live, so a
 * message landing there sits on top of the controls it is talking about.
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'custom';

export interface ToastAction {
  /** Button label. */
  label: string;
  /** Invoked on press; the toast dismisses afterwards. */
  onPress: () => void;
}

export interface ToastOptions {
  /** Seconds on screen before auto-dismiss. */
  duration?: number;
  /** Optional trailing action button. */
  action?: ToastAction;
  /** Only meaningful for `toast.show`; the typed helpers set this. */
  type?: ToastType;
}

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
  action?: ToastAction;
}

// Five seconds for every type. The old split (3s for success, 2s for the
// rest) came from the native port and was tuned for one-word confirmations;
// anything that asks the reader to take something in — let alone act on it —
// was gone before it had been read. A caller that genuinely needs longer can
// still pass `duration`.
const DEFAULT_DURATION: Record<ToastType, number> = {
  success: 5,
  error: 5,
  warning: 5,
  info: 5,
  custom: 5,
};

// --- Imperative bridge -----------------------------------------------------
// The host registers a sink on mount. Anything emitted before that is
// buffered and flushed once the host attaches, so callers never have to
// care whether the host has rendered yet.

let nextId = 1;
let sink: ((item: ToastItem) => void) | null = null;
let dismissSink: (() => void) | null = null;
const buffer: ToastItem[] = [];

function emit(message: string, type: ToastType, opts: ToastOptions = {}): void {
  const text = String(message || '').trim();
  if (!text) return;
  const item: ToastItem = {
    id: nextId++,
    message: text,
    type,
    duration: opts.duration ?? DEFAULT_DURATION[type],
    action: opts.action,
  };
  if (sink) sink(item);
  else buffer.push(item);
}

export const toast = {
  success(message: string, opts?: ToastOptions): void {
    emit(message, 'success', opts);
  },
  error(message: string, opts?: ToastOptions): void {
    emit(message, 'error', opts);
  },
  warning(message: string, opts?: ToastOptions): void {
    emit(message, 'warning', opts);
  },
  info(message: string, opts?: ToastOptions): void {
    emit(message, 'info', opts);
  },
  show(message: string, opts?: ToastOptions): void {
    emit(message, opts?.type ?? 'custom', opts);
  },
  dismiss(): void {
    dismissSink?.();
  },
};

// --- Icons -----------------------------------------------------------------

function ToastIcon({ type }: { type: ToastType }): ReactElement | null {
  if (type === 'custom') return null;
  const path: Record<Exclude<ToastType, 'custom'>, ReactNode> = {
    success: <path d="M5 8.5l2.2 2.2L11 6.5" />,
    error: (
      <>
        <path d="M5.8 5.8l4.4 4.4" />
        <path d="M10.2 5.8l-4.4 4.4" />
      </>
    ),
    warning: (
      <>
        <path d="M8 4.6v3.6" />
        <path d="M8 10.8v.05" />
      </>
    ),
    info: (
      <>
        <path d="M8 7.4v4" />
        <path d="M8 5v.05" />
      </>
    ),
  };
  return (
    <span className={`ds-toast-icon is-${type}`} aria-hidden>
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {path[type as Exclude<ToastType, 'custom'>]}
      </svg>
    </span>
  );
}

// --- Host ------------------------------------------------------------------

const EXIT_MS = 280;
const SWIPE_DISMISS_PX = 44;

/**
 * Mount exactly once near the app root. Owns the on-screen toast, the
 * pending queue, the auto-dismiss timer, and swipe-to-dismiss.
 */
export function ToastHost(): ReactElement | null {
  const [current, setCurrent] = useState<ToastItem | null>(null);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const [dragY, setDragY] = useState(0);

  const queueRef = useRef<ToastItem[]>([]);
  const currentRef = useRef<ToastItem | null>(null);
  const timerRef = useRef<number | null>(null);
  const dragStartRef = useRef<number | null>(null);

  currentRef.current = current;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // present() is referenced by beginLeave() and vice versa, so keep
  // them as refs to dodge the declaration-order / stale-closure dance.
  const presentRef = useRef<(item: ToastItem) => void>(() => {});
  const beginLeaveRef = useRef<() => void>(() => {});

  const present = useCallback((item: ToastItem) => {
    clearTimer();
    setDragY(0);
    setPhase('in');
    setCurrent(item);
    timerRef.current = window.setTimeout(() => {
      beginLeaveRef.current();
    }, item.duration * 1000);
  }, [clearTimer]);
  presentRef.current = present;

  const beginLeave = useCallback(() => {
    if (!currentRef.current) return;
    clearTimer();
    setPhase('out');
    window.setTimeout(() => {
      setCurrent(null);
      currentRef.current = null;
      const next = queueRef.current.shift();
      if (next) presentRef.current(next);
    }, EXIT_MS);
  }, [clearTimer]);
  beginLeaveRef.current = beginLeave;

  // Register the imperative sink and flush anything buffered pre-mount.
  useEffect(() => {
    sink = (item: ToastItem) => {
      if (currentRef.current) queueRef.current.push(item);
      else presentRef.current(item);
    };
    dismissSink = () => beginLeaveRef.current();
    if (buffer.length) {
      const pending = buffer.splice(0, buffer.length);
      const [first, ...rest] = pending;
      queueRef.current.push(...rest);
      presentRef.current(first);
    }
    return () => {
      sink = null;
      dismissSink = null;
      clearTimer();
    };
  }, [clearTimer]);

  if (!current) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    dragStartRef.current = e.clientY;
    clearTimer();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragStartRef.current === null) return;
    const dy = e.clientY - dragStartRef.current;
    // The toast hangs from the top, so it is thrown UP to dismiss: dragY is
    // negative and downward drag is ignored (there is nothing below to go to).
    setDragY(dy < 0 ? dy : 0);
  };
  const onPointerUp = () => {
    if (dragStartRef.current === null) return;
    const dy = dragY;
    dragStartRef.current = null;
    if (dy < -SWIPE_DISMISS_PX) {
      beginLeave();
    } else {
      setDragY(0);
      // restart the dismiss countdown — the swipe was a no-op
      timerRef.current = window.setTimeout(() => beginLeave(), current.duration * 1000);
    }
  };

  const dragging = dragStartRef.current !== null;
  const style = dragY < 0
    ? { transform: `translateY(${dragY}px)`, opacity: Math.max(0, 1 - Math.abs(dragY) / 120), transition: dragging ? 'none' : undefined }
    : undefined;

  const node = (
    <div className="ds-toast-layer" role="status" aria-live="polite">
      <div
        className={`ds-toast is-${phase}${current.action ? ' has-action' : ''}`}
        style={style}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="ds-toast-row">
          <ToastIcon type={current.type} />
          <span className="ds-toast-msg">{current.message}</span>
        </div>
        {current.action && (
          <button
            type="button"
            className="ds-toast-action"
            onClick={() => {
              current.action?.onPress();
              beginLeave();
            }}
          >
            {current.action.label}
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
